import { Configuration, LogLevel, PublicClientApplication, RedirectRequest } from "@azure/msal-browser";

import { isUserSignedIn } from "./auth";
import { LOGGED_IN, LOG_IN_STATUS, LOG_IN_METHODS } from "../utils";
import { UserLoginInformation } from "./auth-utils";
import { CLIENT_ID } from "../env-vars";

const accessScope = `api://${window.location.host}/${CLIENT_ID}/access_as_user`;
const loginRequest: RedirectRequest = {
  scopes: [accessScope],
  extraScopesToConsent: ["user.read"],
};

const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: "https://login.microsoftonline.com/common",
    redirectUri: `https://${window.location.host}/fallbackauthdialog.html`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage", // Needed to avoid "User login is required" error.
    storeAuthStateInCookie: true, // Recommended to avoid certain IE/Edge issues.
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

const publicClientApp: PublicClientApplication = new PublicClientApplication(msalConfig);

let loginDialog: Office.Dialog = null;
let homeAccountId = null;
let processToken = null;

Office.onReady(() => {
  if (Office.context.ui.messageParent) {
    publicClientApp
      .handleRedirectPromise()
      .then(handleResponse)
      .catch((error) => {
        console.log(error);
        Office.context.ui.messageParent(JSON.stringify({ status: "failure", result: error }));
      });
    // The very first time the add-in runs on a developer's computer, msal.js hasn't yet
    // stored login data in localStorage. So a direct call of acquireTokenRedirect
    // causes the error "User login is required". Once the user is logged in successfully
    // the first time, msal data in localStorage will prevent this error from ever hap-
    // pening again; but the error must be blocked here, so that the user can login
    // successfully the first time. To do that, call loginRedirect first instead of
    // acquireTokenRedirect.
    if (isUserSignedIn()) {
      publicClientApp.acquireTokenRedirect(loginRequest);
    } else {
      // This will login the user and then the (response.tokenType === "id_token")
      // path in authCallback below will run, which sets localStorage.loggedIn to "yes"
      // and then the dialog is redirected back to this script, so the
      // acquireTokenRedirect above runs.
      publicClientApp.loginRedirect(loginRequest);
    }
  }
});

function handleResponse(response) {
  if (response.tokenType === "id_token") {
    UserLoginInformation.setLoggedIn(LOGGED_IN.YES);
  } else {
    console.log("token type is:" + response.tokenType);
    Office.context.ui.messageParent(
      JSON.stringify({
        status: LOG_IN_STATUS.SUCCESS,
        result: response.accessToken,
        accountId: response.account.homeAccountId,
      }),
    );
  }
}

function showLoginPopup(url) {
  let fullUrl = location.protocol + "//" + location.hostname + (location.port ? ":" + location.port : "") + url;

  Office.context.ui.displayDialogAsync(fullUrl, { height: 60, width: 30 }, (result) => {
    console.log("Dialog has initialized. Wiring up events");
    loginDialog = result.value;
    loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processMessage);
  });
}

export async function dialogFallback(processUserToken) {
  // Attempt to acquire token silently if user is already signed in.

  const accounts = publicClientApp.getAllAccounts();
  const account = accounts.find((acc) => acc.homeAccountId === homeAccountId);

  if (homeAccountId !== null && UserLoginInformation.getLoggedIn() === LOGGED_IN.YES) {
    const result = await publicClientApp.acquireTokenSilent({ ...loginRequest, account });

    if (result !== null && result.accessToken !== null) {
      processUserToken(result.accessToken);
    }
  } else {
    // We fall back to Dialog API for any error.
    processToken = processUserToken;
    const url = "/fallbackauthdialog.html";
    showLoginPopup(url);
  }
}
// This handler responds to the success or failure message that the pop-up dialog receives from the identity provider
// and access token provider.
async function processMessage(arg) {
  // Uncomment to view message content in debugger, but don't deploy this way since it will expose the token.
  // console.log("Message received in processMessage: " + JSON.stringify(arg));

  let messageFromDialog = JSON.parse(arg.message);

  if (messageFromDialog.status === LOG_IN_STATUS.SUCCESS) {
    // We now have a valid access token.
    loginDialog.close();

    // Configure MSAL to use the signed-in account as the active account for future requests.
    const homeAccount = publicClientApp.getAccountByHomeId(messageFromDialog.accountId);

    if (homeAccount) {
      homeAccountId = messageFromDialog.accountId; // Track the account id for future silent token requests.
      publicClientApp.setActiveAccount(homeAccount);
    }

    const token = messageFromDialog.result;
    processToken(token, LOG_IN_METHODS.MSAL);
  } else if (messageFromDialog.error === undefined && messageFromDialog.result.errorCode === undefined) {
    //TODO: handle error
    // Need to pick the user to use to auth
  } else {
    //TODO: handler error
    // Something went wrong with authentication or the authorization of the web application.
    loginDialog.close();
    UserLoginInformation.setLogInStatus(messageFromDialog.status);
    UserLoginInformation.setLogInMethod("");
    UserLoginInformation.setLoggedIn(LOGGED_IN.NO);

    if (messageFromDialog.error) {
      console.log(JSON.stringify(messageFromDialog.error.toString()));
      // showMessage(JSON.stringify(messageFromDialog.error.toString()));
    } else if (messageFromDialog.result) {
      console.log(JSON.stringify(messageFromDialog.result.errorMessage.toString()));
      // showMessage(JSON.stringify(messageFromDialog.result.errorMessage.toString()));
    }
  }
}

export { publicClientApp, loginRequest, msalConfig };
